// --- START OF FULLY CORRECTED FILE src/components/FiltrosBusqueda.tsx ---

import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Card, Grid } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { AsyncSelect, ControlledTextField } from "../../components/forms";
import { useLoadOptions } from '../../hooks/useLoadOptions';
import { HttpSystemsNewByGroup } from '../../http/group/httpSystemsNewByGroup';
import { z } from '../../lib';
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, SectorComercialFields } from '../../types';
import { SistemaTipo } from '../../types/enum/enum';
import { SistemaPrincipalFields } from '../../types/workflow/cache';
import { HttpCacheOperacion } from '../sistema/api/httpCacheOperacion';
import { useLCTCClienteSubTipos, useLCTCClienteTipos, useLCTCUnidadNegocioTipos } from '../sistema/sistemas/lctc/hooks/useLCTCCache';

export const FormFiltrosFields = {
    Pais: 'codPais',
    NumeroCuenta: 'numeroCuenta',
    RazonSocial: 'razonSocial',
    SectorComercial: 'sectorComercial',
    UnidadNegocio: 'codUnidadNegocio',
    ClienteTipo: 'codClienteTipo',
    ClienteSubTipo: 'codClienteSubTipo',
} as const;

export const schemaFiltrosBusqueda = z.object({
    [FormFiltrosFields.Pais]: z.number().nullable().default(null),
    [FormFiltrosFields.NumeroCuenta]: z.string().nullable().default(null),
    [FormFiltrosFields.RazonSocial]: z.string().nullable().default(null),
    [FormFiltrosFields.SectorComercial]: z.number().nullable().default(null),
    [FormFiltrosFields.UnidadNegocio]: z.number().nullable().default(null),
    [FormFiltrosFields.ClienteTipo]: z.number().nullable().default(null),
    [FormFiltrosFields.ClienteSubTipo]: z.number().nullable().default(null),
});

export type FormFiltros = z.infer<typeof schemaFiltrosBusqueda>;

type FieldName = keyof FormFiltros;

interface FiltrosBusquedaProps {
    formFields: FieldName[];
    mensajeTipo: SistemaTipo;
    onFilterChange: (filtros: FormFiltros) => void;
    debounceFields?: FieldName[]; // que campos se actualizan con logica debounce
}

const DEBOUNCE_DELAY = 500;

export function FiltrosBusqueda({
    formFields,
    mensajeTipo,
    onFilterChange,
    debounceFields = [FormFiltrosFields.NumeroCuenta, FormFiltrosFields.RazonSocial]
}: FiltrosBusquedaProps) {

    const { control, watch, resetField } = useForm<FormFiltros>({
        resolver: zodResolver(schemaFiltrosBusqueda),
        defaultValues: {
            codPais: null,
            numeroCuenta: null,
            razonSocial: null,
            sectorComercial: null,
            codUnidadNegocio: null,
            codClienteTipo: null,
            codClienteSubTipo: null
        }
    });

    const debouncedOnFilterChange = useDebounceCallback(onFilterChange, DEBOUNCE_DELAY);

    const watchPais = useWatch({ control, name: FormFiltrosFields.Pais });
    const watchUnidadNegocio = useWatch({ control, name: FormFiltrosFields.UnidadNegocio });
    const watchClienteTipo = useWatch({ control, name: FormFiltrosFields.ClienteTipo });

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            const safeValue = schemaFiltrosBusqueda.parse(value);

            if (name && debounceFields?.includes(name)) {
                debouncedOnFilterChange(safeValue);
            } else {
                onFilterChange(safeValue);
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, onFilterChange, debouncedOnFilterChange, debounceFields]);

    useEffect(() => {
        if (formFields.includes(FormFiltrosFields.UnidadNegocio)) resetField(FormFiltrosFields.UnidadNegocio);
        if (formFields.includes(FormFiltrosFields.ClienteTipo)) resetField(FormFiltrosFields.ClienteTipo);
        if (formFields.includes(FormFiltrosFields.ClienteSubTipo)) resetField(FormFiltrosFields.ClienteSubTipo);
    }, [watchPais, resetField]);

    useEffect(() => {
        if (formFields.includes(FormFiltrosFields.ClienteTipo)) resetField(FormFiltrosFields.ClienteTipo);
        if (formFields.includes(FormFiltrosFields.ClienteSubTipo)) resetField(FormFiltrosFields.ClienteSubTipo);
    }, [watchUnidadNegocio, resetField]);

    useEffect(() => {
        if (formFields.includes(FormFiltrosFields.ClienteSubTipo)) resetField(FormFiltrosFields.ClienteSubTipo);
    }, [watchClienteTipo, resetField]);

    const opcionesSistemaPrincipal = () => HttpSystemsNewByGroup.getSystemPrincipals()
        .then(sis => (
            sis.map(opcion => (
                {
                    id: opcion[EntityWithIdFields.Id],
                    descripcion: opcion[EntityWithIdAndDescriptionFields.Description],
                    icon: <Avatar src={opcion[SistemaPrincipalFields.SrcIcono]} sx={{ height: '20px', width: '20px' }} />
                }
            ))
        ));

    const opcionesSectorComercial = useCallback(() => {
        if (watchPais) {
            return HttpCacheOperacion.getSectoresComercialesPorPaisyTipoMensaje(watchPais, mensajeTipo);
        }

        return Promise.resolve([]);
    }, [watchPais, mensajeTipo]);

    // TODO: obtener el codSistema real o hacer un nuevo endpoint para traer todas las unidad de negocio solo por pais
    const codSistemaAux = 75;
    const { unidadNegocioTipos, isLoading: isLoadingUnidadNegocio, isError: isErrorUnidadNegocio, error: errorUnidadNegocio } = useLCTCUnidadNegocioTipos(watchPais, codSistemaAux);
    const loadUnidadNegocio = useLoadOptions({ data: unidadNegocioTipos, loading: isLoadingUnidadNegocio, isError: isErrorUnidadNegocio, error: errorUnidadNegocio });

    const { clienteTipos, isLoading: isLoadingClienteTipos, isError: isErrorClienteTipos, error: errorClienteTipos } = useLCTCClienteTipos(watchUnidadNegocio);
    const loadClienteTipos = useLoadOptions({ data: clienteTipos, loading: isLoadingClienteTipos, isError: isErrorClienteTipos, error: errorClienteTipos });

    const { clienteSubTipos, isLoading: isLoadingClienteSubTipos, isError: isErrorClienteSubTipos, error: errorClienteSubTipos } = useLCTCClienteSubTipos(watchClienteTipo);
    const loadClienteSubTipos = useLoadOptions({ data: clienteSubTipos, loading: isLoadingClienteSubTipos, isError: isErrorClienteSubTipos, error: errorClienteSubTipos });

    return (
        <Card sx={{ p: 1, pt: 2 }}>
            <Grid container spacing={2}>
                {formFields.includes(FormFiltrosFields.Pais) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <AsyncSelect
                            control={control}
                            name={FormFiltrosFields.Pais}
                            label='País'
                            loadOptions={opcionesSistemaPrincipal}
                            fullWidth
                            nullOptionLabel={'Todos'}
                            onlyOpenOnArrow
                            valueField={'id'}
                            min
                        />
                    </Grid>
                )}

                {formFields.includes(FormFiltrosFields.NumeroCuenta) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <ControlledTextField
                            control={control}
                            name={FormFiltrosFields.NumeroCuenta}
                            label='Nro. Cuenta'
                            fullWidth
                            min
                        />
                    </Grid>
                )}

                {formFields.includes(FormFiltrosFields.RazonSocial) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <ControlledTextField
                            control={control}
                            name={FormFiltrosFields.RazonSocial}
                            label='Razón Social'
                            fullWidth
                            min
                        />
                    </Grid>
                )}

                {formFields.includes(FormFiltrosFields.SectorComercial) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <AsyncSelect
                            control={control}
                            name={FormFiltrosFields.SectorComercial}
                            label='Sector Comercial'
                            loadOptions={opcionesSectorComercial}
                            valueField={SectorComercialFields.SectorComercialCodigo}
                            fullWidth
                            disabled={!watchPais}
                            min
                            nullOptionLabel={'Todos'}
                        />
                    </Grid>
                )}

                {formFields.includes(FormFiltrosFields.UnidadNegocio) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <AsyncSelect
                            control={control}
                            name={FormFiltrosFields.UnidadNegocio}
                            label='Unidad de Negocio'
                            loadOptions={loadUnidadNegocio}
                            fullWidth
                            disabled={!watchPais}
                            nullOptionLabel={'Todas'}
                            min
                        />
                    </Grid>
                )}

                {formFields.includes(FormFiltrosFields.ClienteTipo) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <AsyncSelect
                            control={control}
                            name={FormFiltrosFields.ClienteTipo}
                            label='Tipo Cliente'
                            loadOptions={loadClienteTipos}
                            fullWidth
                            disabled={!watchUnidadNegocio}
                            nullOptionLabel={'Todos'}
                            key={`cliente-tipo-${watchClienteTipo}`}
                            min
                        />
                    </Grid>
                )}

                {formFields.includes(FormFiltrosFields.ClienteSubTipo) && (
                    <Grid item xs={12} sm={6} md={4}>
                        <AsyncSelect
                            control={control}
                            name={FormFiltrosFields.ClienteSubTipo}
                            label='Sub-Tipo Cliente'
                            loadOptions={loadClienteSubTipos}
                            fullWidth
                            disabled={!watchClienteTipo}
                            nullOptionLabel={'Todos'}
                            key={`cliente-subtipo-${watchClienteTipo}`}
                            min
                        />
                    </Grid>
                )}

            </Grid>
        </Card>
    );
}
